/**
 * PVP System Constants
 *
 * Centralized constants for PVP battles, sessions, and discount system.
 * Used across backend and frontend for consistent behavior.
 */

// =====================================================
// Battle Configuration
// =====================================================

/** Evolution stage used for PVP battles display (stage 2 - balanced combat form) */
export const PVP_LAB_DISPLAY_STAGE = 2;

/**
 * Default level used for PVP Lab battles (level 5 - early-game). At level 5 both fighters
 * get exactly 1 recover use (see getMaxRecoverUsesForLevel: 1 use below level 6), keeping
 * the free trial simple for newcomers.
 */
export const PVP_LAB_DISPLAY_LEVEL = 5;

/**
 * **THE COMBAT LEVEL CEILING — EVERY MODE, THE CAMPAIGN INCLUDED. Pets above this fight as this
 * level.**
 *
 * It was named `COMBAT_MAX_LEVEL` and the name is most of why this was wrong: it read as a rule
 * about pvp, so the campaign was given a lift out of it (`createSquadBattle` took a `maxLevel`
 * and the campaign route passed `CAMPAIGN_MAX_LEVEL`, 100, *"so finale bosses aren't clamped to
 * the ranked cap"*). That made the campaign the only mode fighting on an uncapped curve, and
 * measured, the uncapped curve is a different game (`card-loadout-audit`, both fighters at the
 * same level, tuning log Row 47):
 *
 * | level | typical hit | burst p99 | rounds | the deck: 1 / 2 / 3 / full commit, of a bar |
 * |---|---|---|---|---|
 * | 5 | 16.4% ✓ | 53.6% ✓ | 8.2 | 17.1 · 17.4 · 20.5 · 26.3 |
 * | **20** | **20.5% ✓** | **63.0% ✓** | **8.1** | **21.7 · 22.4 · 23.5 · 33.3** |
 * | 50 | 28.0% ✓ | **72.0% ✗** | 6.9 | 29.4 · 29.5 · 30.5 · 32.9 |
 * | 100 | **33.5% ✗** | **78.2% ✗** | 5.8 | **36.4 · 36.9 · 37.1 · 37.0** |
 *
 * Criterion 10's burst band breaks around level 45 and criterion 15's around 80; by 100 one card,
 * two, three and a full commit are all worth the same share of a bar, so the deck has stopped
 * existing. **Level 20 is the healthiest row in that table** — every criterion in band, the
 * longest fights, and the widest copy ladder — which is what makes one ceiling at 20 a fix by
 * CONSTRUCTION rather than a number to keep re-tuning as worlds are added.
 *
 * **What it costs, stated plainly: the campaign loses LEVEL as its escalation axis.** World bands
 * still author rivals from 10 to 100 and those levels are still what the player's own per-world
 * cap is derived from, but from World 2 onward both sides fight at 20, so a later world is not a
 * harder FIGHT for level reasons. The campaign's escalation has to come from the axes it already
 * has — squad size and `opponentCount`, the evolution caps per node, and the rival's card
 * loadout. Criterion 20's floor is what holds this honest: it fails if levelling stops meaning
 * anything at all, and it is measured on the 1-{@link COMBAT_MAX_LEVEL} span now rather than on
 * a span the game does not play.
 *
 * Applied through {@link combatLevel} at every point a level enters combat scaling — the two
 * battle services, the engine, and every balance harness — so no mode can be handed a lift again.
 */
export const COMBAT_MAX_LEVEL = 20;

/**
 * **CLAMP A PET'S LEVEL TO THE ONE THE FIGHT USES.** The single funnel for
 * {@link COMBAT_MAX_LEVEL}.
 *
 * It exists because the cap was previously applied by three different call sites passing a
 * `maxLevel` argument down, and the campaign simply passed a bigger one. A pure function every
 * caller shares cannot be opted out of by an argument, and it also floors at 1 — a level 0 or a
 * missing level scaled to a negative bonus before now.
 */
export function combatLevel(level: number): number {
  return Math.min(Math.max(1, Math.floor(level)), COMBAT_MAX_LEVEL);
}

/**
 * ELO-based level caps for Ranked PVP.
 * Each entry: pets with elo < threshold fight at levelCap.
 * Sorted ascending by threshold; last entry is the default (no upper bound).
 */
export const RANKED_LEVEL_CAPS = [
  { maxElo: 1400, levelCap: 5 },
  { maxElo: 1700, levelCap: 10 },
  { maxElo: Infinity, levelCap: 20 },
] as const;

/**
 * Returns the ranked level cap for a given ELO rating.
 * < 1400 → 5 | 1400–1699 → 10 | ≥ 1700 → 20
 */
export function getRankedLevelCap(elo: number): number {
  for (const { maxElo, levelCap } of RANKED_LEVEL_CAPS) {
    if (elo < maxElo) return levelCap;
  }
  return COMBAT_MAX_LEVEL;
}

/**
 * Level-gap damage multiplier factor, per level of gap beyond {@link LEVEL_GAP_THRESHOLD}:
 * `multiplier = 1 + (gap - threshold) * K`, up for the higher level and down for the lower.
 *
 * 0.02, raised from 0.01 (tuning log Row 75): an eight-level lead was worth a 5% cut in damage
 * taken, which no player could feel. At 0.02 the same lead deals ×1.10 and takes ×0.90.
 */
export const LEVEL_GAP_DAMAGE_K = 0.02;

/** Minimum level gap before the damage multiplier kicks in */
export const LEVEL_GAP_THRESHOLD = 3;

/** Level gap at which opponent is considered dangerous (skull emoji, red name) */
export const LEVEL_GAP_DANGER = 5;

/** Maximum number of turns before battle ends in a tie (hard cap, no HP comparison) */
export const MAX_BATTLE_TURNS = 10;

/** Time before inactive battles are cleaned up (1 hour) */
export const BATTLE_CLEANUP_TIMEOUT_MS = 60 * 60 * 1000;

/** Time before inactive battles are marked as losses (1 minute) */
export const BATTLE_INACTIVITY_TIMEOUT_MS = 60 * 1000;

/** Interval for checking stale battles on server (30 seconds) */
export const BATTLE_EXPIRY_CHECK_INTERVAL_MS = 30 * 1000;

/** Time to show inactivity warning before auto-abandon (10 seconds before timeout) */
export const BATTLE_INACTIVITY_WARNING_MS = BATTLE_INACTIVITY_TIMEOUT_MS - 10 * 1000;

/** Time to keep completed battles in storage for result retrieval (5 minutes) */
export const COMPLETED_BATTLE_RETENTION_MS = 5 * 60 * 1000;

/** Maximum action lock streak (4 same actions in a row locks the action) */
export const ACTION_LOCK_STREAK = 4;

// =====================================================
// Discount System Configuration
// =====================================================

/** Number of challenge battles required to earn mint discount (must win 4 battles) */
export const DISCOUNT_ELIGIBLE_BATTLES = 4;

/** Default discount percentage for PVP discount links */
export const DISCOUNT_PERCENT_DEFAULT = 75;

/** Discount link validity period (24 hours) */
export const DISCOUNT_LINK_EXPIRY_MS = 24 * 60 * 60 * 1000;

/** Rarities eligible for PVP discount (Common=0, Rare=1) */
export const DISCOUNT_ELIGIBLE_RARITIES = [0, 1] as const;

// =====================================================
// Battle Modes
// =====================================================

/**
 * Battle Mode String Constants
 * Use these instead of magic strings throughout the codebase.
 * 
 * Example usage:
 *   if (mode === BATTLE_MODE.PRACTICE) { ... }
 *   createBattle(..., BATTLE_MODE.CHALLENGE)
 */
export const BATTLE_MODE = {
  /** Admin-only testing mode - Internal sandbox for debugging/testing */
  PRACTICE: 'practice',
  /** Public Battle Lab mode - Win 4 battles for 75% mint discount */
  CHALLENGE: 'challenge',
  /** Competitive PvP - ELO-based matchmaking with Trophy ratings */
  RANKED: 'ranked',
  /** Training mode - Battle with owned Suikamons to earn XP */
  TRIAL: 'trial',
  /** Telegram PvE - Turn-based battle via Telegram bot */
  TELEGRAM_BATTLE: 'telegram_battle',
  /** Telegram PvP - Player vs Player via Telegram bot (future) */
  TELEGRAM_PVP: 'telegram_pvp',
  /** Squad Ranked - 3v3 trainer battles with separate trainer-level ELO */
  SQUAD_RANKED: 'squad_ranked',
  /** Campaign - PvE story mode: 3v3 squad battles vs rival trainers on a map */
  CAMPAIGN: 'campaign',
} as const;

/**
 * PVP Battle Mode Types
 * 
 * ┌─────────────────────────────────────────────────────────┐
 * │ PUBLIC MODES (Accessible via /play page)                │
 * ├─────────────────────────────────────────────────────────┤
 * │ 🧪 Battle Lab    → "challenge" mode                      │
 * │    • Win 4 battles to earn 75% mint discount             │
 * │    • Pre-mint only, counts toward discount eligibility   │
 * │                                                           │
 * │ ⚔️  Training      → "trial" mode                          │
 * │    • Battle with your owned pets                         │
 * │    • Free to play, Win = +25 XP                          │
 * │                                                           │
 * │ 🏆 Ranked        → "ranked" mode                          │
 * │    • ELO-based matchmaking, Trophy ratings               │
 * │    • Free to play, Win = +25 XP                          │
 * └─────────────────────────────────────────────────────────┘
 * 
 * ┌─────────────────────────────────────────────────────────┐
 * │ ADMIN-ONLY MODE (Accessible via /admin page only)       │
 * ├─────────────────────────────────────────────────────────┤
 * │ 🔧 Debug Testing → "practice" mode                       │
 * │    • Internal testing sandbox                            │
 * │    • Custom species/levels/AI strategies                 │
 * │    • Does NOT count toward any progression               │
 * │    • NOT exposed to public users                         │
 * └─────────────────────────────────────────────────────────┘
 */
export type PvpBattleMode = 'practice' | 'challenge' | 'ranked' | 'trial' | 'telegram_battle' | 'telegram_pvp' | 'squad_ranked' | 'campaign';

/**
 * **THE MODES FOUGHT THREE AGAINST THREE — which TABLE a battle's row lives in.**
 *
 * A 1v1 battle is a row in `pvp_battles`; a squad battle is a row in `pvp_squad_battles`. Every
 * surface that reads battles back — the live audit, its SQL, the `/battle-audit` API — has to
 * know which table a mode lands in, and until now each one worked it out by comparing a mode id
 * to `'campaign'` by hand. That is the shape of bug where a mode added later is silently read
 * from the wrong table and simply reports as "no battles".
 *
 * So the split is declared once, here with the modes themselves, and
 * `pvpBattleModeTables.test.ts` asserts every {@link PvpBattleMode} is classified — a new mode
 * fails the test rather than quietly defaulting to 1v1.
 */
export const SQUAD_BATTLE_MODES = [BATTLE_MODE.SQUAD_RANKED, BATTLE_MODE.CAMPAIGN] as const;

export type SquadBattleMode = (typeof SQUAD_BATTLE_MODES)[number];

/** Is this mode fought 3v3 (so its row is in `pvp_squad_battles`)? Never compare ids by hand. */
export function isSquadMode(mode: string): mode is SquadBattleMode {
  return (SQUAD_BATTLE_MODES as readonly string[]).includes(mode);
}

// =====================================================
// Battle Mode Combat Tuning
// =====================================================

/**
 * Neutral damage multiplier — a mode set to this fights at its species' real damage.
 */
export const DEFAULT_DAMAGE_MULTIPLIER = 1;

/**
 * A fight with cards on it hits 10% harder: BOTH fighters' Attack and Magic damage is
 * multiplied by this. Symmetric, so it changes the pace and not the matchup — the battle
 * ends sooner, which is what a fight carrying a hand of cards wants. Heals, shields and
 * special-effect values (bleed, execute, exploit bonuses) are untouched.
 *
 * Named for CARDS, not for Trial: the tuning was written for the card game and travels with
 * it to every mode that deals one.
 */
export const CARDS_DAMAGE_MULTIPLIER = 1.1;

/**
 * Neutral HP multiplier — a mode set to this fights on its species' real health bar.
 */
export const DEFAULT_HP_MULTIPLIER = 1;

/**
 * **A FIGHT WITH CARDS ON IT NEEDS A LONGER BAR.** Both fighters' maximum HP is multiplied by
 * this in every mode that deals cards.
 *
 * The bar was sized for a game whose only damage was a species' own Attack and Magic. The deck
 * then added a SECOND damage source on top of it — a stack of up to four copies, on every
 * gesture, every round — and the bar never moved. `CARDS_DAMAGE_MULTIPLIER` moved it the wrong
 * way, making the two skills hit 10% harder still, because it was tuned before the deck landed
 * and "a fight carrying a hand of cards should end sooner" was a guess about pace rather than a
 * measurement of one.
 *
 * The measurement says the guess was wrong, and it is the whole reason criteria 12-15 exist
 * (`turnFeel.ts`). Before this knob, on the full sweep:
 *
 *  • a TYPICAL hit took **36% of a health bar** — three of them and you are dead, so nobody is
 *    ever behind long enough to come back from it;
 *  • the median winner finished on **99% of their own bar**, having been hit essentially not at
 *    all: a fight that short is decided by who loses the first two exchanges, not by play;
 *  • one exchange in a hundred took **75%** of a bar (criterion 10's burst band is 70%).
 *
 * Every one of those is the same finding — the bar is too short for the game now being played
 * on it — and this is the one knob that answers it without changing what anything is WORTH.
 * Multiplying the bar scales the skills, the cards, the bleeds and the heals against it all at
 * once, symmetrically, so no archetype, card or tier moves relative to any other. Nerfing card
 * values instead would have re-priced the deck to fix the pace.
 *
 * **It is applied at battle creation like every other mode knob** — the engine stays mode-blind
 * (`battleModeTuning.ts`), so nothing downstream needs to know a bar was stretched.
 */
export const CARDS_HP_MULTIPLIER = 2.0;

/**
 * Recover uses per fighter in a cards battle — the same number at any level, for both sides.
 * In a cards-off mode, recover uses stay level-based (see `getMaxRecoverUsesForLevel`); this is
 * a ceiling applied on top of that, so a level-5 fighter still gets its single use.
 *
 * **Why it is 2 and not 1.** A loadout has four slots, one per suit, and the recover slot is
 * bought with the same shards as the other three. At ONE use the recover slot was not a card
 * you played, it was a card you held: the other three suits are committed as often as the fight
 * lasts, and recover was capped at a single commit across a 5.5-turn battle. That is a hard
 * ceiling of ~18% of commits for a quarter of the loadout — which is why criterion 11's floor
 * of 15% could not be reached by pricing recover cards, and why three attempts to move it with
 * {@link EFFECT_WHEEL_FIRE_ODDS} were inert. The cap was the binding constraint, not the values.
 *
 * Measured paired at 60 battles/matchup on seeds 1337,42, 1 use → 2:
 *
 * | criterion | 1 use | 2 uses |
 * |---|---|---|
 * | 5 no dead cards | UNSTABLE (unmeasurable) | ✓ 1.00x / 1.00x |
 * | 6 no auto-includes | UNSTABLE (unmeasurable) | ✓ 11.8% |
 * | 11 least-used suit | ✗ 10.0% | ✗ 13.4% |
 * | 3 archetype spread | +10.8 | +10.2 |
 * | 2 roster stddev | 4.3% | 4.1% |
 * | 10 avg turns · draws | 5.5 · 2.2% | 6.0 · 1.6% |
 * | G2 buying helps | ✗ 1.3% | ✗ 10.3% |
 *
 * Criteria 5 and 6 were not failing at 1 use — they were UNMEASURABLE, because recover cards
 * were barely dealt often enough to observe. Doubling the budget is what put them in the sample.
 * No criterion was lost, and the campaign got slightly harder (per-attempt win rate 51.1% →
 * 49.7%), not easier.
 *
 * A third use is NOT free: fights lengthen (5.5 → 6.0 turns at two), and criterion 10's draw
 * rate is the thing that breaks first. Raise it only with a paired run in hand.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * **THREE, AND AT THREE THIS CAP NO LONGER BINDS. That is the decision, not an accident.**
 *
 * It is applied as `Math.min(getMaxRecoverUsesForLevel(level), this)` and the level ladder tops
 * out at 3, so the min now resolves to the level budget at every level in the game: **cards mode
 * and cards-off mode grant the same recover budget, and it is the one you earned by levelling.**
 *
 * **What made a separate, tighter budget necessary was stalling**, and stalling is now answered
 * by a better mechanic. Recover beats all three gestures, so without a brake a cards fight could
 * be healed into a stalemate — the cap was that brake. `PRESSURE_PER_ROUND` is a far better one:
 * every blow grows for the rest of the fight while healing does not, so a long fight becomes
 * unsurvivable for BOTH fighters and no amount of recover can wait it out. A brake that scales
 * beats a brake that counts.
 *
 * **And the tight cap had a measured cost.** Criterion 11 (is every suit worth committing to?)
 * read the recover suit at **13.6% against a 15.0% floor** — the game's fourth move was one a
 * player had least reason to pick, which is a suit's worth of decisions deleted. At three it
 * reads **15.3%**, and criterion 14 (a fight can be turned around) rose with it: the third
 * recover is not a stall any more, it is the comeback.
 *
 * **The constant stays** rather than being deleted along with the branch, for two reasons: it is
 * the knob to re-tighten with if pressure ever stops holding that line, and a `Math.min` that
 * currently resolves one way is a rule that still reads correctly if the level ladder ever grows
 * a fourth step. `battleEngine.recoverBudget.test.ts` pins the min itself rather than either side
 * of it, so none of this has to be rewritten when the number moves again.
 */
// Typed as `number`, NOT left to infer the literal: this is a tuning knob, and every place that
// phrases it for players ("one of the N they get") needs to branch on singular vs plural. Under a
// literal type those branches are compile errors on whichever value is not current, so the UI
// would have to be rewritten every time the knob moves.
export const CARDS_MAX_RECOVER_USES: number = 3;

/** No mode cap on recover uses — the level-based value stands. */
export const NO_RECOVER_USE_CAP = null;

/**
 * Battle Mode Configuration
 * Centralized config for each mode with labels, descriptions, and metadata.
 */
/**
 * `cardsEnabled` is the Battle Cards gate.
 *
 * Cards are the way Suikamon battles resolve. Every public mode deals them — the 1v1 arena
 * (Trial, Ranked, Challenge, Practice) and the 3v3 squad modes (Squad Ranked, Campaign).
 *
 * The TELEGRAM modes are the exception, and are cards-off: they ran on a separate
 * chat-driven engine with no pitch step to shape a hand in. That engine was RETIRED in Beta
 * 20.33 (the bot became a companion that never fights); the two modes stay here only so
 * recorded `telegram_*` battles and the simulators that read them keep their labels.
 *
 * `damageMultiplier`, `hpMultiplier` and `recoverUseCap` are the other three per-mode combat
 * knobs, and every cards mode carries the cards tuning. They stay SEPARATE fields rather than
 * being derived from `cardsEnabled`, so a mode can run cards at neutral damage without a
 * refactor.
 *
 * The knobs are applied ONCE, at battle creation (see `scaleCombatConfigForMode` /
 * `getModeMaxRecoverUses` in `battleModeTuning.ts`), so the engine keeps reading plain
 * numbers and stays mode-blind. Read them through `getModeDamageMultiplier` /
 * `getModeHpMultiplier` / `getModeRecoverUseCap` — never by comparing a mode id to a literal
 * by hand.
 */
export const BATTLE_MODE_CONFIG = {
  /** Practice mode - ADMIN-ONLY testing mode (not exposed to public) */
  [BATTLE_MODE.PRACTICE]: {
    id: BATTLE_MODE.PRACTICE,
    label: 'Practice',
    description: 'Internal testing mode for admins. Custom battles with any species/level.',
    countsTowardDiscount: false,
    isPublic: false,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
  /** Challenge mode - Public Battle Lab (4 wins for discount) */
  [BATTLE_MODE.CHALLENGE]: {
    id: BATTLE_MODE.CHALLENGE,
    label: 'Challenge',
    description: 'Competitive battles. Win 4 to earn substantial mint discount!',
    countsTowardDiscount: true,
    isPublic: true,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
  /** Ranked mode - Competitive PvP with ELO ratings */
  [BATTLE_MODE.RANKED]: {
    id: BATTLE_MODE.RANKED,
    label: 'Ranked',
    description: 'Competitive matchmaking with ELO rating. Free battles against similarly-ranked opponents.',
    countsTowardDiscount: false,
    isPublic: true,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
  /** Trial mode - Train owned pets for XP rewards */
  [BATTLE_MODE.TRIAL]: {
    id: BATTLE_MODE.TRIAL,
    label: 'Trial',
    description: 'Practice with your owned Suikamons. Win to earn XP!',
    countsTowardDiscount: false,
    isPublic: true,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
  /** Telegram Battle - PvE battles via Telegram bot */
  [BATTLE_MODE.TELEGRAM_BATTLE]: {
    id: BATTLE_MODE.TELEGRAM_BATTLE,
    label: 'Telegram Battle',
    description: 'Turn-based PvE battles in Telegram with Shika narrator.',
    countsTowardDiscount: false,
    isPublic: false,
    cardsEnabled: false,
    damageMultiplier: DEFAULT_DAMAGE_MULTIPLIER,
    hpMultiplier: DEFAULT_HP_MULTIPLIER,
    recoverUseCap: NO_RECOVER_USE_CAP,
  },
  /** Telegram PvP - Future PvP via Telegram bot */
  [BATTLE_MODE.TELEGRAM_PVP]: {
    id: BATTLE_MODE.TELEGRAM_PVP,
    label: 'Telegram PvP',
    description: 'Player vs Player battles in Telegram (coming soon).',
    countsTowardDiscount: false,
    isPublic: false,
    cardsEnabled: false,
    damageMultiplier: DEFAULT_DAMAGE_MULTIPLIER,
    hpMultiplier: DEFAULT_HP_MULTIPLIER,
    recoverUseCap: NO_RECOVER_USE_CAP,
  },
  /** Squad Ranked - 3v3 trainer battles with separate trainer ELO ladder */
  [BATTLE_MODE.SQUAD_RANKED]: {
    id: BATTLE_MODE.SQUAD_RANKED,
    label: 'Squad Ranked',
    description: 'Assemble 3 of your Suikamons and battle a 3-pet squad. Pokémon-style swaps + 1 healing potion. Earn Trainer ELO.',
    countsTowardDiscount: false,
    isPublic: true,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
  /** Campaign - PvE story mode with 3v3 squad battles vs rival trainers */
  [BATTLE_MODE.CAMPAIGN]: {
    id: BATTLE_MODE.CAMPAIGN,
    label: 'Campaign',
    description: 'Rival Trainer Road: travel the map, defeat rival trainers in 3v3 squad battles, and earn XP on first clears. No ELO at stake.',
    countsTowardDiscount: false,
    isPublic: true,
    cardsEnabled: true,
    damageMultiplier: CARDS_DAMAGE_MULTIPLIER,
    hpMultiplier: CARDS_HP_MULTIPLIER,
    recoverUseCap: CARDS_MAX_RECOVER_USES,
  },
} as const;

// =====================================================
// Trial Battle Configuration
// =====================================================

/** XP reward for winning a trial battle */
export const TRIAL_BATTLE_XP_REWARD = 25;

// =====================================================
// UI Animation Constants
// =====================================================

/** Spinner animation interval in milliseconds */
export const SPINNER_INTERVAL_MS = 100;

/** Spinner animation duration before slowdown in milliseconds */
export const SPINNER_DURATION_MS = 2000;

/** Spinner slowdown increment per step in milliseconds */
export const SPINNER_SLOWDOWN_INCREMENT_MS = 50;

/** Spinner maximum delay before final selection in milliseconds */
export const SPINNER_MAX_DELAY_MS = 400;

/** Delay before navigation after selection in milliseconds */
export const POST_SELECTION_DELAY_MS = 500;

/** Duration to show battle result notification in milliseconds */
export const NOTIFICATION_DISPLAY_MS = 5000;

// =====================================================
// AI Difficulty Levels
// =====================================================

export type AiStrategy = 'random' | 'pro';

export const AI_STRATEGY_CONFIG = {
  random: {
    id: 'random' as const,
    label: 'Random',
    description: 'Pure random baseline. Respects forced actions.',
  },
  pro: {
    id: 'pro' as const,
    label: 'Pro',
    description: 'Simulation-backed tactical play. No mistakes.',
  },
} as const;

// =====================================================
// Session Management
// =====================================================

/** Time before anonymous sessions are cleaned up (7 days) */
export const SESSION_CLEANUP_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000;

// =====================================================
// Validation Constants
// =====================================================

/** Valid species IDs (0-9, matching speciesConfig.ts) */
export const MIN_SPECIES_ID = 0;
export const MAX_SPECIES_ID = 9;

/** Valid evolution stages (1-7) */
export const MIN_STAGE = 1;
export const MAX_STAGE = 7;

/** Valid combat actions */
export const VALID_COMBAT_ACTIONS = ['attack', 'magic', 'dodge', 'recover'] as const;
export type ValidCombatAction = (typeof VALID_COMBAT_ACTIONS)[number];

// =====================================================
// Ranked PVP / ELO System
// =====================================================

/** Starting ELO rating for new pets (chess-like baseline) */
export const STARTING_ELO = 1200;

/**
 * Ranked season label is OWNED BY THE DATABASE (table `ranked_season_state`).
 * Bumping the label happens atomically as part of the admin season-reset
 * endpoint — no code deploy required. Use the API:
 *   • Backend: `getCurrentRankedSeason()` in database/rankedSeasonRepository.ts
 *   • Frontend: `fetchCurrentRankedSeason()` / `/api/v1/seasons/current`
 */

/**
 * Level-based trophy brackets applied at season reset.
 * Replaces the legacy flat reset to STARTING_ELO so low-level pets aren't
 * thrown into matchmaking against veterans on day one. Ordered descending —
 * the first bracket whose minLevel a pet meets wins. Cap is level 20 (the
 * canonical PVP balance level — we don't reward hoarding ELO beyond it).
 */
export const SEASON_RESET_ELO_BRACKETS = [
  { minLevel: 20, elo: 1400 },
  { minLevel: 15, elo: 1300 },
  { minLevel: 10, elo: 1200 },
  { minLevel: 5, elo: 1100 },
  { minLevel: 1, elo: 1000 },
] as const;

/** Returns the season-reset trophy value for a pet at the given level. */
export function bracketForLevel(level: number): number {
  for (const bracket of SEASON_RESET_ELO_BRACKETS) {
    if (level >= bracket.minLevel) return bracket.elo;
  }
  return SEASON_RESET_ELO_BRACKETS[SEASON_RESET_ELO_BRACKETS.length - 1].elo;
}

/** ELO K-factor (determines rating volatility, ~20% lower than chess-standard 32) */
export const ELO_K_FACTOR = 26;

/**
 * Ranked progression bias for player-controlled pets.
 * Wins get a flat bonus and losses are softened so the leaderboard climb
 * stays accessible — active players earn ground steadily instead of grinding
 * a zero-sum ladder.
 */
export const PLAYER_ELO_WIN_BONUS = 5;
export const PLAYER_ELO_LOSS_MULTIPLIER = 0.5;

/** Major ranked tiers that permanently protect player ELO from dropping below them once claimed. */
export const PROTECTED_RANKED_TIER_THRESHOLDS = [1500, 1900, 2100] as const;

/**
 * Tiered override of the win bonus / loss cushion based on the player's CURRENT
 * trophy rating (before the match). Thresholds intentionally mirror the Gold/
 * Diamond/Crown cutoffs in ELO_TIERS below. Bronze and Silver keep the full
 * accessibility cushion (PLAYER_ELO_WIN_BONUS / PLAYER_ELO_LOSS_MULTIPLIER) so
 * new and mid-ladder players climb steadily. From Gold up, wins shrink toward
 * zero and losses hit harder than the raw ELO formula — climbing past Gold has
 * to come from actually winning close matches, not from playtime alone.
 * Sorted descending by minElo; the last entry (0) is the default/fallback.
 */
export const TROPHY_TIER_ELO_SCALING = [
  { minElo: 2400, winBonus: 0, lossMultiplier: 1.5 },   // Crown
  { minElo: 2100, winBonus: 1, lossMultiplier: 1.0 },   // Diamond
  { minElo: 1900, winBonus: 2, lossMultiplier: 0.75 },  // Gold
  { minElo: 0,     winBonus: PLAYER_ELO_WIN_BONUS, lossMultiplier: PLAYER_ELO_LOSS_MULTIPLIER }, // Bronze/Silver
] as const;

/**
 * Returns the win bonus / loss multiplier to apply, based on the player's
 * current trophy rating. See TROPHY_TIER_ELO_SCALING for the tier breakdown.
 */
export function getTrophyTierEloScaling(elo: number): { winBonus: number; lossMultiplier: number } {
  for (const tier of TROPHY_TIER_ELO_SCALING) {
    if (elo >= tier.minElo) {
      return { winBonus: tier.winBonus, lossMultiplier: tier.lossMultiplier };
    }
  }
  return TROPHY_TIER_ELO_SCALING[TROPHY_TIER_ELO_SCALING.length - 1];
}

/** Progressive ELO matchmaking ranges (backend tries narrow first, expands if needed) */
export const ELO_MATCHMAKING_RANGES = [100, 200, 300] as const;

/** @deprecated Use ELO_MATCHMAKING_RANGES instead. Kept for reference. */
export const ELO_MATCHMAKING_RANGE = 300;

/** Minimum ELO rating (floor) */
export const MIN_ELO = 0;

/** Maximum ELO rating (ceiling) */
export const MAX_ELO = 5000;

/** Default number of top pets shown in leaderboard */
export const LEADERBOARD_TOP_COUNT = 10;

/** Maximum leaderboard size that can be requested */
export const LEADERBOARD_MAX_LIMIT = 50;

/** XP reward for winning a ranked battle */
export const RANKED_BATTLE_XP_REWARD = 25;

/** Suika cost to enter a ranked battle (challenger only, charged on confirmed match, no refund on loss) */
export const SUIKA_COST_RANKED = 1;

/* RANKED_OPPONENT_LOADING_MS lived here — an unused leftover superseded by the
   MATCH_SEARCH_* range below, which the search window actually reads. */

/** Countdown before battle starts in milliseconds (5 seconds) */
export const RANKED_BATTLE_COUNTDOWN_MS = 5000;

/**
 * How long the "Searching for opponent" / "Entering battle" window stays up, as a
 * range (3–4 s). Real matchmaking usually resolves faster than this; the floor is
 * deliberate, because an overlay that flashes for 200 ms reads as a glitch while a
 * few seconds of searching reads as a real match hunt — and gives the player time to
 * actually read the tip. The exact wait is rolled inside the range so repeat runs
 * don't feel metronomic.
 */
export const MATCH_SEARCH_MIN_MS = 3000;
export const MATCH_SEARCH_MAX_MS = 4000;

/** How long each tip holds before the search window rotates to the next one. */
export const MATCH_SEARCH_TIP_ROTATE_MS = 2600;

/**
 * How long the search window holds on the REVEALED matchup (your fighter vs the
 * opponent that was just found) before the arena takes over. Long enough to read the
 * opponent's name and see who you drew, short enough that it never feels like a
 * second wait. Ranked uses its own longer {@link RANKED_BATTLE_COUNTDOWN_MS} because
 * it counts down on screen; every other mode uses this beat.
 *
 * The point of the beat is continuity: without it the window closes onto the screen
 * the player STARTED from — searching, then apparently going backwards — before the
 * battle finally opens.
 */
export const MATCH_SEARCH_REVEAL_MS = 1600;

// =====================================================
// ELO Tier System
// =====================================================

/**
 * ELO tier thresholds and display emoji.
 * Silver starts at 1500 — above the highest level-bracket reset value (1400 for Lv 20+)
 * so newly-reset pets never auto-claim Silver without playing.
 */
export const ELO_TIERS = [
  { minElo: 2400, emoji: '👑', label: 'Crown' },
  { minElo: 2100, emoji: '💎', label: 'Diamond' },
  { minElo: 1900, emoji: '🥇', label: 'Gold' },
  { minElo: 1500, emoji: '🥈', label: 'Silver' },
  { minElo: 0,    emoji: '🥉', label: 'Bronze' },
] as const;

/**
 * Returns the ELO tier emoji for a given ELO rating.
 * 🥉 < 1500 | 🥈 1500 | 🥇 1900 | 💎 2100 | 👑 2400+
 */
export function getEloTierEmoji(eloRating: number): string {
  for (const tier of ELO_TIERS) {
    if (eloRating >= tier.minElo) {
      return tier.emoji;
    }
  }
  return '🥉';
}

/**
 * Returns the full ELO tier object for a given ELO rating.
 */
export function getEloTier(eloRating: number): (typeof ELO_TIERS)[number] {
  for (const tier of ELO_TIERS) {
    if (eloRating >= tier.minElo) {
      return tier;
    }
  }
  return ELO_TIERS[ELO_TIERS.length - 1];
}

// =====================================================
// Ranked Milestone Rewards
// =====================================================

/**
 * Milestone rewards granted when a pet crosses a tier threshold for the first time
 * (one-time per pet per season, no re-claiming on drop/re-climb). A milestone pays
 * {@link RANKED_MILESTONE_SHARDS_MIN}-{@link RANKED_MILESTONE_SHARDS_MAX} Soul Shards of a random
 * Suikamon — NOT an egg (it used to be a breeding token). Derived from ELO_TIERS — excludes
 * Bronze (the starting tier). Sorted descending by minElo (same order as ELO_TIERS).
 */
export const RANKED_MILESTONE_REWARDS = ELO_TIERS.filter(tier => tier.minElo > 0);

/** A tier milestone pays at least this many Soul Shards… */
export const RANKED_MILESTONE_SHARDS_MIN = 3;
/** …and at most this many. */
export const RANKED_MILESTONE_SHARDS_MAX = 4;

/** How many Soul Shards one tier milestone pays: a whole number, MIN to MAX inclusive. */
export function rollRankedMilestoneShards(random: () => number = Math.random): number {
  const span = RANKED_MILESTONE_SHARDS_MAX - RANKED_MILESTONE_SHARDS_MIN + 1;
  return RANKED_MILESTONE_SHARDS_MIN + Math.min(span - 1, Math.floor(random() * span));
}

/**
 * Sentinel milestone_level values for ranked breeding tokens in the breeding_tokens table.
 * These are well above max pet level (100) and satisfy the CHECK(milestone_level > 0) constraint.
 * Each maps to a ranked tier ELO threshold.
 */
export const RANKED_BREEDING_MILESTONES: Record<number, number> = {
  1500: 9001, // Silver
  1900: 9002, // Gold
  2100: 9003, // Diamond
  2400: 9004, // Crown
};

/**
 * Ranked milestone breeding tokens are stored with sentinel milestone levels so
 * they do not collide with normal level-based breeding milestones.
 */
export const RANKED_BREEDING_MILESTONE_LEVELS = new Set(
  Object.values(RANKED_BREEDING_MILESTONES)
);

/**
 * Returns true when a breeding token milestone level belongs to ranked PvP.
 */
export function isRankedBreedingMilestoneLevel(milestoneLevel: number): boolean {
  return RANKED_BREEDING_MILESTONE_LEVELS.has(milestoneLevel);
}

/**
 * Guaranteed rarity for each ranked milestone sentinel level.
 * Silver (9001) → Rare (1), all higher tiers (9002–9004) → Epic (2).
 */
export const RANKED_MILESTONE_GUARANTEED_RARITY: Record<number, number> = {
  9001: 0, // Silver → Common
  9002: 1, // Gold   → Rare
  9003: 2, // Diamond → Epic
  9004: 2, // Crown  → Epic
};

/**
 * Returns the guaranteed rarity number for a ranked milestone sentinel level,
 * or null if the level is not a ranked milestone.
 */
export function getRankedMilestoneGuaranteedRarity(milestoneLevel: number): number | null {
  return RANKED_MILESTONE_GUARANTEED_RARITY[milestoneLevel] ?? null;
}

/**
 * Returns the sentinel milestone_level for the breeding_tokens table given an ELO threshold.
 */
export function getRankedBreedingMilestoneLevel(eloThreshold: number): number {
  return RANKED_BREEDING_MILESTONES[eloThreshold] ?? eloThreshold;
}

/**
 * Returns all tier thresholds that were crossed going from oldElo to newElo.
 * Only returns thresholds where oldElo < threshold <= newElo (upward crossings).
 * Sorted ascending by threshold.
 */
export function getNewTiersCrossed(oldElo: number, newElo: number): typeof RANKED_MILESTONE_REWARDS[number][] {
  if (newElo <= oldElo) return [];
  return [...RANKED_MILESTONE_REWARDS]
    .filter(m => oldElo < m.minElo && newElo >= m.minElo)
    .sort((a, b) => a.minElo - b.minElo);
}

/**
 * Returns the next unclaimed milestone, given already-claimed thresholds.
 * Returns null if all milestones are claimed.
 */
export function getNextUnclaimedMilestone(
  claimedThresholds: number[]
): (typeof RANKED_MILESTONE_REWARDS)[number] | null {
  const claimedSet = new Set(claimedThresholds);
  // Sort ascending to find the lowest unclaimed milestone
  const ascending = [...RANKED_MILESTONE_REWARDS].sort((a, b) => a.minElo - b.minElo);
  for (const milestone of ascending) {
    if (!claimedSet.has(milestone.minElo)) {
      return milestone;
    }
  }
  return null;
}

/**
 * Calculates progress percentage toward the next tier threshold.
 * The bar starts at STARTING_ELO (all pets begin there), so the effective floor
 * for Bronze is STARTING_ELO rather than 0. This means 1300 ELO = 50% toward Silver.
 * Returns 100 if at or above Crown tier.
 */
export function getTierProgress(elo: number): number {
  // Find current tier and next tier
  const ascending = [...ELO_TIERS].sort((a, b) => a.minElo - b.minElo);
  for (let i = ascending.length - 1; i >= 0; i--) {
    if (elo >= ascending[i].minElo) {
      // Found current tier
      const nextTierIndex = i + 1;
      if (nextTierIndex >= ascending.length) {
        return 100; // Already at highest tier
      }
      // Use STARTING_ELO as effective floor — no pet can be below that
      const effectiveFloor = Math.max(ascending[i].minElo, STARTING_ELO);
      const nextFloor = ascending[nextTierIndex].minElo;
      const range = nextFloor - effectiveFloor;
      if (range <= 0) return 100;
      return Math.min(100, Math.round(((elo - effectiveFloor) / range) * 100));
    }
  }
  return 0;
}

// =====================================================
// Squad Battle (3v3) Configuration
// =====================================================

/** Number of pets per squad in Squad Ranked battles */
export const SQUAD_SIZE = 3;

/** Healing potions available per squad per battle (shared across all 3 pets) */
export const SQUAD_POTIONS_PER_BATTLE = 1;

/**
 * Fraction of max HP restored by a healing potion.
 * Pill rules: using a pill consumes the whole turn and cannot be interrupted.
 */
export const SQUAD_POTION_HEAL_PERCENT = 0.8;

/**
 * Fraction of the attacker's real move damage dealt to a Suikamon swapped in
 * VOLUNTARILY while the opponent attacks. A full hit (1) means the incoming pet
 * takes the same damage it would take while fighting (base + element-advantage
 * bonus on magic), so the number matches a normal turn. Forced swaps (after a
 * KO) are free.
 */
export const SQUAD_SWAP_IN_DAMAGE_FRACTION = 1;

/**
 * Legacy config kept for compatibility. Potion interrupt damage is disabled,
 * so this value is currently unused.
 */
export const SQUAD_POTION_EXPOSED_DAMAGE_FRACTION = 1;

/**
 * Reference turn count for squad battle pacing (commentary/UI only).
 * Squad battles have NO turn cap or tiebreaker — they run until one side is
 * fully knocked out.
 */
export const MAX_SQUAD_BATTLE_TURNS = 30;

/**
 * Care reward: a pet that enters a squad/campaign battle in the HAPPY
 * condition (all care stats ≥ 25, see `PetCondition.HAPPY`) fights with this
 * flat bonus added to its attack AND magic damage. AI and rival squads never
 * get it — taking care of your Suikamons pays off in battle.
 */
export const HAPPY_PET_DAMAGE_BONUS = 5;

/** Maximum number of squad ranked battles per wallet per UTC day (anti-grinding cap) */
export const SQUAD_RANKED_DAILY_LIMIT = 20;

/** Suika cost per pet in a Squad Ranked battle (charged once per squad pet on battle creation) */
export const SUIKA_COST_SQUAD = 1;

/** Suika cost per pet in a Campaign battle (charged once per squad pet on battle creation) */
export const SUIKA_COST_CAMPAIGN = 1;

/**
 * Does this battle mode deal Battle Cards? The single read of the rollout gate — never
 * compare a mode id to `TRIAL` by hand.
 */
export function areCardsEnabled(mode: PvpBattleMode): boolean {
  return BATTLE_MODE_CONFIG[mode]?.cardsEnabled ?? false;
}

/**
 * **THE PUBLIC MODES THAT DEAL CARDS, NAMED — for any surface that has to say WHERE.**
 *
 * The prose half of {@link areCardsEnabled}, and it exists because the prose got it wrong.
 * Eight rows of the in-game help table said Battle Cards were "Trial battles only" — the
 * rollout's first mode, typed into a string once and then left there while Challenge, Ranked,
 * Squad Ranked and the whole Campaign turned them on. A player reading the help was told the
 * cards in front of them did not exist in the mode they were in.
 *
 * Derived from {@link BATTLE_MODE_CONFIG} and filtered to `isPublic`, so a mode that gains or
 * loses cards rewrites the sentence, and an admin-only mode never leaks into a player-facing
 * one. Config order, which is the order the modes shipped.
 */
export const CARDS_ENABLED_MODE_LABELS: readonly string[] = Object.values(BATTLE_MODE_CONFIG)
  .filter((config) => config.cardsEnabled && config.isPublic)
  .map((config) => config.label);

/**
 * The first level at which web battles deal cards. Cards are now part of every web battle,
 * including a brand-new pet's first fight, so this is the game's minimum combat level.
 */
export const CARDS_MIN_LEVEL = 1;

/**
 * The same list as one phrase, for a table cell: `A, B and C`.
 *
 * Includes the minimum level so the player-facing table derives the complete answer.
 */
export const CARDS_ENABLED_WHERE: string = `${
  CARDS_ENABLED_MODE_LABELS.length > 1
    ? `${CARDS_ENABLED_MODE_LABELS.slice(0, -1).join(', ')} and ${
        CARDS_ENABLED_MODE_LABELS[CARDS_ENABLED_MODE_LABELS.length - 1]
      }`
    : (CARDS_ENABLED_MODE_LABELS[0] ?? 'no modes')
}, from Lv${CARDS_MIN_LEVEL}`;

/**
 * Does THIS battle deal cards? Every declared battle mode does. `playerLevel` remains in the
 * signature so battle constructors have one stable policy call, but level never removes the
 * hand: base cards are still cards and the player must see and confirm them.
 *
 * Every caller that builds a battle reads this, never `areCardsEnabled` alone.
 *
 * @param _playerLevel Retained so every battle constructor shares one stable policy call.
 */
export function areCardsEnabledForBattle(
  mode: PvpBattleMode | undefined,
  _playerLevel: number | undefined
): boolean {
  return mode !== undefined && areCardsEnabled(mode);
}

/**
 * The Attack/Magic damage multiplier this mode fights at (both fighters). The single read of
 * the mode damage knob — never compare a mode id to `TRIAL` by hand.
 */
export function getModeDamageMultiplier(mode: PvpBattleMode): number {
  return BATTLE_MODE_CONFIG[mode]?.damageMultiplier ?? DEFAULT_DAMAGE_MULTIPLIER;
}

/**
 * The maximum-HP multiplier this mode fights at (both fighters). The single read of the mode
 * HP knob — see {@link CARDS_HP_MULTIPLIER} for why a cards mode has one.
 */
export function getModeHpMultiplier(mode: PvpBattleMode): number {
  return BATTLE_MODE_CONFIG[mode]?.hpMultiplier ?? DEFAULT_HP_MULTIPLIER;
}

/**
 * This mode's hard cap on recover uses per fighter, or `NO_RECOVER_USE_CAP` when the
 * level-based value applies. The single read of the mode recover knob.
 */
export function getModeRecoverUseCap(mode: PvpBattleMode): number | null {
  return BATTLE_MODE_CONFIG[mode]?.recoverUseCap ?? NO_RECOVER_USE_CAP;
}
